import type { CustomerType, RealEstateContact } from "../../@types/sharedSchema";
import type { CrmContactRow } from "../../@types/webotServer";

/**
 * WHAT A LOOKED-UP CONTACT IS: the fields of the contact document that the Postgres mirror
 * can supply, under the document's OWN field names.
 *
 * ── Why document names ──────────────────────────────────────────────────────────────
 *
 * Everything in the dashboard that shows a contact was written against `RealEstateContact`
 * — `contact.callerFirstName`, `contact.tags`, `contact.customerBudget`. Those screens used
 * to get their contact out of `useLegacyContactsBook().allContacts` (the whole book, downloaded); they now
 * get it from `useContactsLookup`. Speaking the document's names means the reading code
 * does not change, only where the object comes from.
 *
 * ── Why it is a `Pick`, and what the type is for ────────────────────────────────────
 *
 * A mirror row carries about thirty fields; a contact document carries well over a hundred
 * (the ID number, the address, every amenity flag, the AI call analysis, …). A summary is
 * therefore NOT a contact, and the type says so: it is assignable to a function that
 * declares the handful of fields it reads, and it is NOT assignable to one that takes a
 * whole `RealEstateContact`.
 *
 * That refusal is the feature. The predecessor of this file cast its projection
 * `as unknown as RealEstateContact`, and the failure it invited is silent: a template
 * placeholder renders empty, or — far worse — an edit form opened on the projection writes
 * the projection back and every field the mirror does not carry is gone from the document.
 * A card or dialog about ONE contact reads the live document (`useContactByPhone`).
 *
 * ── Adding a field ──────────────────────────────────────────────────────────────────
 *
 * It must come from the mirror: add it to the server's projection (`buildProjection` /
 * `ContactsListRow`), mirror it onto `CrmContactRow`, then add the key here and map it in
 * {@link rowToContactSummary}. Never fill one from `allContacts`.
 */
export type ContactSummary = {
  /** The document id — the phone in E.164. What the lookup store is keyed by. */
  id: string;
} & Required<Pick<RealEstateContact, "contactId" | "userId">> &
  Pick<
    RealEstateContact,
    | "callerFirstName"
    | "callerLastName"
    | "contactFullName"
    | "additionalNotes"
    | "contactOrigin"
    | "crmStatus"
    | "crmSubStatus"
    | "crmIsHot"
    | "customerType"
    | "customerTypes"
    | "tags"
    | "interestedInLocations"
    | "interestedInPropertyTypes"
    | "sellingListingIds"
    | "rentingOutListingIds"
    | "customerBudgetRent"
    | "roomsMin"
    | "roomsMax"
    | "optedOutOfBulkMessages"
    | "isInSpamDatabase"
    | "lastBulkMessageReceivedTime"
  > & {
    /** Absent when the contact has no budget — the document types it as always present. */
    customerBudget?: number;
    /**
     * ISO strings, as everywhere else in the dashboard's memory (the document TYPE says
     * `Date`; the legacy book has always held strings, and consumers read them as such).
     */
    createdTime?: string;
    updatedTime?: string;
  };

/** Every key a summary carries — the single list the adapters below are checked against. */
export const CONTACT_SUMMARY_KEYS = [
  "id",
  "contactId",
  "userId",
  "callerFirstName",
  "callerLastName",
  "contactFullName",
  "additionalNotes",
  "contactOrigin",
  "crmStatus",
  "crmSubStatus",
  "crmIsHot",
  "customerType",
  "customerTypes",
  "tags",
  "interestedInLocations",
  "interestedInPropertyTypes",
  "sellingListingIds",
  "rentingOutListingIds",
  "customerBudget",
  "customerBudgetRent",
  "roomsMin",
  "roomsMax",
  "optedOutOfBulkMessages",
  "isInSpamDatabase",
  "lastBulkMessageReceivedTime",
  "createdTime",
  "updatedTime"
] as const satisfies readonly (keyof ContactSummary)[];

/**
 * THE adapter: a mirror row → the summary every reader gets.
 *
 * One function, so a field cannot be mapped two ways. Empty values are ABSENT rather than
 * `""` / `[]`-as-a-string, because the reading code tests truthiness (`contact.tags?.length`,
 * `contact.callerFirstName || phone`) and was written against documents, where an unset
 * field is missing.
 *
 * Three mappings that are not a rename:
 *
 *  - `lastBulkMessageReceivedTime` ← the row's `lastBulkMessageSentTime`: the later of the
 *    extension's stamp and the official campaign's. The document field of that name holds
 *    only the campaign's, and the legacy book overwrote it with only the extension's — each
 *    rail's "messaged recently" skip was blind to the other. Read from here, it sees both.
 *  - `customerType` ← the first of `customerTypes`: the legacy single-value mirror, which is
 *    exactly how the write path derives it.
 *  - `customerBudgetMin` is NOT carried. The row's `budgetMin` is `COALESCE(min, budget)` —
 *    the lower end of the range for overlap filtering — so it cannot say whether the
 *    realtor set a minimum at all.
 */
export const rowToContactSummary = (row: CrmContactRow): ContactSummary => {
  const customerTypes = (row.customerTypes ?? []) as CustomerType[];

  return {
    id: row.contactId,
    contactId: row.contactId,
    userId: row.ownerUserId,

    callerFirstName: row.firstName ?? undefined,
    callerLastName: row.lastName ?? undefined,
    contactFullName: row.name ?? undefined,

    additionalNotes: row.additionalNotes ?? undefined,
    contactOrigin: row.contactOrigin ?? "",

    crmStatus: (row.crmStatus ?? null) as ContactSummary["crmStatus"],
    crmSubStatus: row.crmSubStatus ?? null,
    crmIsHot: row.crmIsHot,

    customerType: customerTypes[0] ?? null,
    customerTypes,
    tags: row.tags ?? [],

    interestedInLocations: (row.interestedInLocations ?? []) as ContactSummary["interestedInLocations"],
    interestedInPropertyTypes: row.propertyTypes ?? [],
    sellingListingIds: row.sellingListingIds ?? [],
    rentingOutListingIds: row.rentingOutListingIds ?? [],

    customerBudget: row.budget ?? undefined,
    customerBudgetRent: row.budgetRent ?? undefined,
    roomsMin: (row.roomsMin ?? null) as ContactSummary["roomsMin"],
    roomsMax: (row.roomsMax ?? null) as ContactSummary["roomsMax"],

    optedOutOfBulkMessages: row.optedOutOfBulkMessages,
    isInSpamDatabase: row.isInSpamDatabase,
    lastBulkMessageReceivedTime: row.lastBulkMessageSentTime ?? undefined,

    createdTime: row.createdTime ?? undefined,
    updatedTime: row.updatedTime ?? undefined
  };
};

const SUMMARY_KEY_SET: ReadonlySet<string> = new Set(CONTACT_SUMMARY_KEYS);

/**
 * The part of a DOCUMENT edit that a summary can show — for writing a browser-side edit
 * through to the lookup store.
 *
 * A patch is whatever fields a form saved, in document names; this keeps the ones a summary
 * carries and drops the rest. The id keys are never taken from a patch (the store keys the
 * entry, and an edit cannot move it), and a Firestore sentinel — `FieldValue.delete()` is
 * what the write path puts where a field is being cleared — becomes "absent".
 */
export const pickSummaryFields = (patch: Record<string, unknown>): Partial<ContactSummary> => {
  const picked: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (!SUMMARY_KEY_SET.has(key) || key === "id" || key === "contactId" || key === "userId") continue;
    picked[key] = isPlainValue(value) ? value : undefined;
  }
  // The extension's stamp arrives as the nested object; the summary reads the flat time.
  const sent = (patch.lastBulkMessageReceived as { sentTime?: unknown } | undefined)?.sentTime;
  if (typeof sent === "string") picked.lastBulkMessageReceivedTime = sent;
  return picked as Partial<ContactSummary>;
};

/**
 * A contact THIS browser just created, for the rare response that carries no mirror row
 * (the inline mirror sync failed; the scheduled one repairs it within a minute).
 *
 * Built from what was sent, which is everything that is known about a contact that did not
 * exist a moment ago. The alternative — leaving the store saying "not in your book" about a
 * contact the realtor has just saved — is the one wrong answer available here.
 */
export const summaryOfCreatedContact = (
  contactId: string,
  ownerUserId: string,
  fields: Record<string, unknown>
): ContactSummary => ({
  contactOrigin: "",
  customerType: null,
  roomsMin: null,
  roomsMax: null,
  ...pickSummaryFields(fields),
  id: contactId,
  contactId,
  userId: ownerUserId
});

/** A value that can sit in a summary: a primitive, an array, or a plain object — not a class instance. */
const isPlainValue = (value: unknown): boolean => {
  if (value === null || typeof value !== "object") return true;
  if (Array.isArray(value)) return true;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};
